import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute } from '@tanstack/react-router';
import { Col, Row, Space } from 'antd';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { EngagementAlertsPanel } from './components/EngagementAlertsPanel';
import { EngagementLeaderboard } from './components/EngagementLeaderboard';
import { EngagementOverview } from './components/EngagementOverview';
import { LowEngagementAlerts } from './components/LowEngagementAlerts';
import { OrgEngagementDrawer } from './components/OrgEngagementDrawer';

export const Route = createFileRoute('/admin-console/monitoring/engagement/')({
    staticData: {
        ui: {
            title: 'Organisation engagement',
            subtitle: 'Monitor and track organisation engagement metrics to identify at-risk organisations.',
            widthVariant: 'default',
            menuDescription: 'Monitor organisation engagement',
        },
        breadcrumb: {
            title: 'Engagement',
        },
    },
    component: EngagementDashboard,
});

function EngagementDashboard() {
    const { trpc, trpcUtils } = useTrpc();
    const [selectedOrgId, setSelectedOrgId] = useState<bigint | undefined>();

    const { mutate: triggerCalculation, isPending: isCalculating } =
        trpc.admin.stats.calculateEngagementManually.useMutation({
            onSuccess: (data) => {
                message.success(data.message);
                // Invalidate all engagement queries to refresh all components
                trpcUtils.admin.stats.getEngagementLeaderboard.invalidate();
            },
            onError: (error) => {
                errorMessage.show(error);
            },
        });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary>
                <PageLayoutHeader
                    primaryActionAddon={
                        <Button
                            type="primary"
                            icon={<RefreshCw />}
                            loading={isCalculating}
                            onClick={() => triggerCalculation()}
                        >
                            Calculate Engagement Now
                        </Button>
                    }
                />

                <Space orientation="vertical" size="large" className="w-full">
                    <EngagementOverview />

                    {/* Alerts Row - System alerts and at-risk organisations side by side */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <EngagementAlertsPanel onOrgClick={setSelectedOrgId} />
                        </Col>
                        <Col xs={24} lg={12}>
                            <LowEngagementAlerts onOrgClick={setSelectedOrgId} />
                        </Col>
                    </Row>

                    <EngagementLeaderboard onOrgClick={setSelectedOrgId} />
                </Space>

                <OrgEngagementDrawer
                    orgId={selectedOrgId}
                    open={!!selectedOrgId}
                    onClose={() => setSelectedOrgId(undefined)}
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}
